import { AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import type { EscalationData } from '@/lib/api'

const LEVEL_STYLES: Record<string, { ring: string; text: string; glow: string; border: string }> = {
  critical: { ring: '#ef4444', text: 'text-red-400', glow: 'shadow-[0_0_15px_rgba(239,68,68,0.3)]', border: 'border-red-500/40' },
  high: { ring: '#f97316', text: 'text-orange-400', glow: 'shadow-[0_0_10px_rgba(249,115,22,0.2)]', border: 'border-orange-500/30' },
  elevated: { ring: '#eab308', text: 'text-yellow-400', glow: '', border: 'border-yellow-500/30' },
  normal: { ring: '#22c55e', text: 'text-green-400', glow: '', border: 'border-green-500/20' },
}

interface EscalationMeterProps {
  data: EscalationData | null
  loading?: boolean
  onCountryClick?: (countryCode: string) => void
  onScoreClick?: () => void
}

export default function EscalationMeter({ data, loading, onCountryClick, onScoreClick }: EscalationMeterProps) {
  if (loading || !data) {
    return (
      <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded p-3">
        <div className="h-20 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full border-2 border-gray-700/50 animate-pulse" />
        </div>
      </div>
    )
  }

  const style = LEVEL_STYLES[data.level] || LEVEL_STYLES.normal
  const pct = Math.min(data.composite_score, 100)
  const circumference = 2 * Math.PI * 34
  const offset = circumference - (pct / 100) * circumference

  const TrendIcon = data.cyber_escalation.trend === 'rising'
    ? TrendingUp
    : data.cyber_escalation.trend === 'falling'
      ? TrendingDown
      : Minus

  return (
    <div className={`bg-gray-900/90 backdrop-blur border ${style.border} rounded p-3 ${style.glow}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <AlertTriangle className={`w-3 h-3 ${style.text}`} />
        <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono">Escalation</span>
        {onScoreClick && (
          <button
            type="button"
            className={`ml-auto text-[9px] font-mono uppercase tracking-wider ${style.text} opacity-60 hover:opacity-100 transition-opacity cursor-pointer`}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onScoreClick() }}
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault() }}
          >
            ▸ analyze
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* Gauge */}
        <div className="relative w-16 h-16 shrink-0">
          <svg viewBox="0 0 76 76" className="w-full h-full -rotate-90">
            <circle cx="38" cy="38" r="34" fill="none" stroke="#1e293b" strokeWidth="4" />
            <circle
              cx="38" cy="38" r="34" fill="none"
              stroke={style.ring}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              className="transition-all duration-1000"
              style={{ filter: `drop-shadow(0 0 6px ${style.ring}60)` }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-lg font-bold font-mono ${style.text}`}>{Math.round(pct)}</span>
          </div>
          {onScoreClick && (
            <button
              type="button"
              className="absolute inset-0 z-10 rounded-full cursor-pointer hover:bg-white/5 transition-colors"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onScoreClick() }}
              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault() }}
              title="Click for detailed breakdown"
            />
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-1">
          <div className={`text-xs font-bold uppercase tracking-wider ${style.text} font-mono`}>
            {data.level}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-gray-500">
            <TrendIcon className="w-3 h-3" />
            <span className="font-mono">Cyber: {data.cyber_escalation.trend}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-3 text-[9px] text-gray-600 font-mono">
            <span>KEV: <span className="text-gray-400">{data.cyber_escalation.kev_spike.toFixed(0)}</span></span>
            <span>RW: <span className="text-gray-400">{data.cyber_escalation.ransomware_activity.toFixed(0)}</span></span>
            <span>IOC: <span className="text-gray-400">{data.cyber_escalation.ioc_surge.toFixed(0)}</span></span>
            <span>APT: <span className="text-gray-400">{data.cyber_escalation.apt_chatter.toFixed(0)}</span></span>
          </div>
        </div>
      </div>

      {/* Top unstable countries */}
      {data.country_scores.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-800/30">
          <span className="text-[8px] text-gray-700 uppercase tracking-[0.15em] font-mono">Top Instability</span>
          <div className="mt-1 space-y-0.5">
            {data.country_scores.slice(0, 5).map((c) => (
              <div
                key={c.country_code}
                className="flex items-center justify-between text-[10px] font-mono cursor-pointer hover:bg-green-500/10 rounded px-1 -mx-1 transition-colors"
                onClick={() => onCountryClick?.(c.country_code)}
              >
                <span className="text-gray-400 truncate hover:text-green-400 transition-colors">{c.country_name || c.country_code}</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-14 h-1 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(c.instability_score, 100)}%`,
                        background: c.instability_score >= 60 ? '#ef4444' : c.instability_score >= 30 ? '#f97316' : '#eab308',
                      }}
                    />
                  </div>
                  <span className={`text-[9px] w-6 text-right ${
                    c.instability_score >= 60 ? 'text-red-400' : c.instability_score >= 30 ? 'text-orange-400' : 'text-yellow-400'
                  }`}>
                    {c.instability_score.toFixed(0)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
